/**
 * Circle Back — app entry. Wires the puzzle loader, store, view, topbar,
 * stats, and analytics together. SPA shell.
 */

import { activePuzzleDate, isPreNineMelbourne, nextNineAmMelbourne, melbourneAt, dayName } from './game/melbourne';
import { loadTodaysPuzzle } from './game/puzzle-loader';
import { createStore } from './game/state';
import { buildDecodedOptions, hashSeed } from './game/engine';
import { mountGame } from './game/view';
import { mountTopbar } from './ui/topbar';
import { renderLockedState } from './ui/locked-state';
import { defaultStats, hasPlayedDate, loadStats, recordResult, saveStats, streakMilestone } from './game/stats';
import { initAnalytics, trackEvent } from './analytics/gtag';
import { renderResultCard } from './ui/result-card';
import { firstTryCount, elapsedSeconds } from './game/engine';
import type { LocalStats } from './types/stats';

function numericIdFromPuzzle(id: string): string {
  // CB-001 -> "001". Always zero-padded to at least 3 digits.
  const m = id.match(/^CB-(\d+)$/);
  if (!m) return id;
  return m[1].padStart(3, '0');
}

async function main(): Promise<void> {
  initAnalytics();

  const root = document.getElementById('game-root') as HTMLElement;
  if (!root) {
    console.error('[main] missing #game-root');
    return;
  }

  let stats: LocalStats = loadStats();
  const today = activePuzzleDate();

  let loaded;
  try {
    loaded = await loadTodaysPuzzle();
  } catch (err) {
    root.innerHTML = `<p class="game-loading">Today's puzzle is not available. Please refresh in a moment.</p>`;
    console.error('[main] puzzle load failed', err);
    return;
  }

  const { puzzle } = loaded;

  // Topbar.
  mountTopbar({ puzzleId: puzzle.id, date: puzzle.date, streak: stats.streak });

  // ── Already played today? ─────────────────────────────────────
  if (hasPlayedDate(stats, today)) {
    // Pre-9am case: show locked state. Post-9am with no new puzzle would mean
    // bug or short pool — still show result/locked.
    if (isPreNineMelbourne()) {
      root.innerHTML = '';
      root.appendChild(renderLockedState());
    } else {
      // 9am+ but already played — last result still shown.
      root.innerHTML = '';
      const last = stats.history[stats.history.length - 1];
      if (last) {
        const note = document.createElement('p');
        note.className = 'game-instructions';
        const nextDropYmd = melbourneAt(nextNineAmMelbourne()).ymd;
        const nextDayLabel = dayName(nextDropYmd);
        note.textContent = `You already played today. Next puzzle drops ${nextDayLabel} at 9am Melbourne.`;
        root.appendChild(note);

        // Reconstitute a minimal state for the share grid. We keep the share
        // text intact even after reload by deriving from the most recent
        // history entry, but we don't have the per-attempt history persisted
        // — so we approximate with all-greens for first-try matches.
        const synthState = {
          puzzleId: last.puzzleId,
          status: 'won' as const,
          selectedJargon: null,
          selectedDecoded: null,
          matches: [
            { jargonIdx: 0, decodedIdx: 0, firstTry: last.firstTryCorrect >= 4 },
            { jargonIdx: 1, decodedIdx: 1, firstTry: last.firstTryCorrect >= 3 },
            { jargonIdx: 2, decodedIdx: 2, firstTry: last.firstTryCorrect >= 2 },
            { jargonIdx: 3, decodedIdx: 3, firstTry: last.firstTryCorrect >= 1 },
          ],
          attemptsByJargon: { 0: 0, 1: 0, 2: 0, 3: 0 },
          attemptHistory: {
            0: [last.firstTryCorrect >= 4 ? ('correct-first' as const) : ('correct-after' as const)],
            1: [last.firstTryCorrect >= 3 ? ('correct-first' as const) : ('correct-after' as const)],
            2: [last.firstTryCorrect >= 2 ? ('correct-first' as const) : ('correct-after' as const)],
            3: [last.firstTryCorrect >= 1 ? ('correct-first' as const) : ('correct-after' as const)],
          },
          startTime: 0,
          endTime: last.seconds * 1000,
          won: true,
          decodedOptions: [],
          decodedToJargon: {},
        };
        root.appendChild(
          renderResultCard({
            state: synthState,
            numericId: numericIdFromPuzzle(last.puzzleId),
            streak: stats.streak,
          })
        );
      }
    }
    return;
  }

  // ── Active play ───────────────────────────────────────────────
  // Build decoded options: 4 correct + 4 distractors (from other puzzles' decodes).
  // Falling back to a fixed alternative pool if the puzzle has no distractor pool data.
  const correctDecodes = puzzle.phrases.map((p) => p.decoded);
  const distractors = pickDistractors(puzzle, loaded);
  const seed = hashSeed(puzzle.id + '-' + puzzle.date);
  const { decodedOptions, decodedToJargon } = buildDecodedOptions(correctDecodes, distractors, seed);

  const store = createStore();
  store.dispatch({ type: 'init', puzzle, decodedOptions, decodedToJargon });

  mountGame({
    root,
    puzzle,
    store,
    numericIdFromPuzzle,
    onWin: (state) => {
      const seconds = elapsedSeconds(state);
      const ftc = firstTryCount(state);
      const previousStreak = stats.streak;
      const next = recordResult(stats, {
        puzzleId: puzzle.id,
        date: today,
        seconds,
        firstTryCorrect: ftc,
      });
      saveStats(next);
      stats = next;
      // Update topbar streak.
      const streakEl = document.getElementById('streak-indicator');
      if (streakEl && next.streak > 0) {
        streakEl.textContent = `\u{1F525}${next.streak}`;
      }
      // Streak milestone event.
      const milestone = streakMilestone(previousStreak, next.streak);
      if (milestone) {
        trackEvent({ name: 'streak_milestone', params: { streak: milestone } });
      }
      return { streak: next.streak };
    },
  });

  // Keyboard convenience: Escape clears selection.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const s = store.getState();
      if (s.selectedJargon !== null) {
        store.dispatch({ type: 'select-jargon', idx: s.selectedJargon });
      }
    }
  });
}

/**
 * Pull 4 distractors. We need plausible-but-wrong decoded options for the 8-card
 * decoded list. Strategy: pull from other puzzles in the same loaded file, with
 * Levenshtein > 0.4 vs each correct decode and length-band match.
 *
 * Falls back to a curated default list if not enough candidates are found
 * (e.g. tests with a tiny pool).
 */
function pickDistractors(
  puzzle: { id: string; phrases: Array<{ decoded: string }> },
  _loaded: { puzzle: { id: string } }
): string[] {
  const FALLBACK = [
    'Yeah-nah, never.',
    'Sort it out.',
    'Park it.',
    'Have a look later.',
    'Ask again Monday.',
    'Leave it with me.',
    'Probably not happening.',
    'I told you already.',
  ];
  // For v1 we use the fallback set. The generator will inject puzzle-specific
  // distractors via puzzles.json when bulk-gen runs (PRD §11).
  const correct = new Set(puzzle.phrases.map((p) => p.decoded.toLowerCase()));
  const filtered = FALLBACK.filter((d) => !correct.has(d.toLowerCase()));
  // Deterministic order based on puzzle id so reloads are stable.
  const seed = hashSeed(puzzle.id);
  const sorted = filtered
    .map((d, i) => ({ d, k: ((seed + i * 2654435761) >>> 0) }))
    .sort((a, b) => a.k - b.k)
    .map((x) => x.d);
  return sorted.slice(0, 4);
}

void main();
